import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Briefcase, ChevronRight, Clock, Compass, Package, Sparkles, Swords } from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { usePageTitle } from '@/lib/usePageTitle'
import type { Category, HomeHero, ProductSummary, StaticPage } from '@/shared/types'

interface HomeResponse {
  hero: HomeHero
}

interface PublicSettings {
  quiz_enabled: boolean
  shop_name: string
}

export function HomePage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('home')

  const { data: home } = useQuery<HomeResponse>({
    queryKey: ['home'],
    queryFn: () => api.get<HomeResponse>('/home'),
  })

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const { data: categories } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  })

  const { data: products } = useQuery<ProductSummary[]>({
    queryKey: ['products', 'home-new'],
    queryFn: () => api.get<ProductSummary[]>('/products'),
  })

  const { data: pages } = useQuery<StaticPage[]>({
    queryKey: ['pages'],
    queryFn: () => api.get<StaticPage[]>('/pages'),
    staleTime: 300_000,
  })

  const hero = home?.hero
  const heroTitle = hero ? localizedName(lang, hero.title_uk, hero.title_en) : ''
  const heroSubtitle = hero ? localizedName(lang, hero.subtitle_uk, hero.subtitle_en) : ''
  const newest = (products ?? []).slice(0, 8)

  const richTitle = settings?.shop_name
    ? (lang === 'en' ? `${settings.shop_name} — Online Store` : `${settings.shop_name} — Інтернет-магазин`)
    : undefined
  usePageTitle(richTitle)

  return (
    <div className="pb-20">
      {hero && (heroTitle || hero.photo_url) && (
        <div className="relative">
          {hero.photo_url ? (
            <img src={hero.photo_url} alt={heroTitle} className="h-48 w-full object-cover" />
          ) : (
            <div className="h-48 w-full bg-gradient-to-br from-brand-600 to-indigo-700" />
          )}
          <div className="absolute inset-0 flex flex-col items-start justify-end bg-black/30 p-5 text-white">
            {heroTitle && <h1 className="text-xl font-bold">{heroTitle}</h1>}
            {heroSubtitle && <p className="mt-1 text-sm opacity-90">{heroSubtitle}</p>}
            <Link
              to="/catalog"
              className="mt-3 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-gray-900"
            >
              {T.shopNow}
            </Link>
          </div>
        </div>
      )}

      {/* Luxury Scent Compass & Discovery Banner */}
      <div className="mx-4 mt-4 overflow-hidden rounded-2xl border border-[#c9a24b]/40 bg-gradient-to-br from-[#181622] via-[#221e30] to-[#12101a] p-4 text-white shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a24b]/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#d7b171] border border-[#c9a24b]/30">
              <Compass className="h-3 w-3" />
              <span>{lang === 'en' ? 'Interactive Discovery' : 'Інтерактивний компас'}</span>
            </div>
            <h3 className="font-serif text-lg font-bold text-white leading-tight">
              {lang === 'en' ? 'Scent Universe & 5D Radar' : 'Матриця Ароматів & 5D Радар'}
            </h3>
            <p className="text-xs text-neutral-300 leading-relaxed max-w-sm">
              {lang === 'en'
                ? 'Find your signature fragrance by notes, temperature, and sillage. Interactive side-by-side battle & time evolution.'
                : 'Підбір аромату за нотами, шлейфом і температурою. Батл Side-by-Side та піраміда розкриття в часі.'}
            </p>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#c9a24b]/20 border border-[#c9a24b]/40 text-[#d7b171] shadow-inner">
            <Compass className="h-6 w-6 animate-pulse" />
          </div>
        </div>

        {/* Quick Discovery Navigation Chips */}
        <div className="mt-3.5 flex flex-wrap gap-2 border-t border-white/10 pt-3">
          <Link
            to="/scent-finder"
            search={{ tab: 'compass' }}
            className="flex items-center gap-1.5 rounded-xl bg-[#c9a24b] px-3 py-1.5 text-xs font-bold text-[#110f18] hover:bg-[#d4af37] transition shadow"
          >
            <span>🧭 {lang === 'en' ? 'Open Compass' : 'Відкрити Компас'}</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/scent-finder"
            search={{ tab: 'versus' }}
            className="flex items-center gap-1 rounded-xl bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs font-medium text-neutral-200 hover:border-[#c9a24b] transition"
          >
            <Swords className="h-3.5 w-3.5 text-[#06b6d4]" />
            <span>{lang === 'en' ? 'Versus Battle' : 'Батл ароматів'}</span>
          </Link>
          <Link
            to="/scent-finder"
            search={{ tab: 'pyramid' }}
            className="flex items-center gap-1 rounded-xl bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs font-medium text-neutral-200 hover:border-[#c9a24b] transition"
          >
            <Clock className="h-3.5 w-3.5 text-amber-400" />
            <span>{lang === 'en' ? 'Time Pyramid' : 'Піраміда в часі'}</span>
          </Link>
          <Link
            to="/scent-finder"
            search={{ tab: 'capsule' }}
            className="flex items-center gap-1 rounded-xl bg-gradient-to-r from-[#c8a96e]/20 to-amber-500/20 border border-[#c9a24b]/50 px-2.5 py-1.5 text-xs font-semibold text-[#ebd5ab] hover:border-[#c9a24b] transition shadow-sm"
          >
            <Briefcase className="h-3.5 w-3.5 text-[#d7b171]" />
            <span>{lang === 'en' ? 'Capsule 4×5ml' : lang === 'ru' ? 'Гардероб 4×5мл' : 'Гардероб 4×5 мл'}</span>
          </Link>
        </div>
      </div>

      {settings?.quiz_enabled && (
        <Link
          to="/quiz"
          className="mx-4 mt-3 flex items-center justify-between rounded-2xl bg-gradient-to-r from-purple-600 to-pink-500 p-4 text-white shadow-sm"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <span className="font-semibold">{T.quizBannerTitle}</span>
          </div>
          <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium">
            {T.quizBannerCta}
          </span>
        </Link>
      )}

      {categories && categories.length > 0 && (
        <div className="mt-6 px-4">
          <h2 className="mb-2 text-base font-semibold text-gray-800">{T.categoriesTitle}</h2>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to="/catalog"
                search={{ category: cat.id }}
                className="flex w-24 shrink-0 flex-col items-center gap-1.5"
              >
                {cat.photo_url ? (
                  <img
                    src={cat.photo_url}
                    alt={localizedName(lang, cat.name_uk, cat.name_en)}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-2xl">
                    📦
                  </div>
                )}
                <span className="line-clamp-1 text-center text-xs text-gray-700">
                  {localizedName(lang, cat.name_uk, cat.name_en)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {newest.length > 0 && (
        <div className="mt-6 px-4">
          <h2 className="mb-2 text-base font-semibold text-gray-800">{T.newProductsTitle}</h2>
          <ul className="grid grid-cols-2 gap-3">
            {newest.map((product) => {
              const name = localizedName(lang, product.name_uk, product.name_en)
              return (
                <li
                  key={product.id}
                  className="min-w-0 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
                >
                  <Link to="/catalog/$productSlug" params={{ productSlug: product.slug || String(product.id) }}>
                    {product.photo_url ? (
                      <img src={product.photo_url} alt={name} className="h-32 w-full object-cover" />
                    ) : (
                      <div className="flex h-32 items-center justify-center bg-gray-50 text-3xl">
                        <Package className="h-8 w-8 opacity-40" />
                      </div>
                    )}
                    <div className="min-w-0 p-2.5">
                      <p className="line-clamp-2 break-words text-sm font-medium leading-tight">{name}</p>
                      <p className="mt-1 text-sm font-bold text-brand-600">{formatPrice(product.price_uah)}</p>
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {pages && pages.length > 0 && (
        <div className="mt-8 flex flex-wrap justify-center gap-x-4 gap-y-2 border-t border-gray-100 px-4 pt-4">
          {pages.map((page) => (
            <Link
              key={page.slug}
              to="/pages/$slug"
              params={{ slug: page.slug }}
              className="text-xs text-gray-400"
            >
              {localizedName(lang, page.title_uk, page.title_en)}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
